import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import FormInput from "../../components/CustomComponents/Input/formInput";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import useDarkMode from "../../hooks/useDarkMode";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import "./requisition-pages.css";

const CreateRequisition = () => {
  const navigate = useNavigate();
  const compID = useSelector((state) => state.auth.user?.userData?.compID);
  const currentUserName = useSelector((state) => state.auth.user?.userData?.userName);
  const profileData = useSelector((state) => state.auth.profileData);
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [loading, setLoading] = useState(false);
  const [requisitionNumber, setRequisitionNumber] = useState("");
  const [dueDate, setDueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const fetchBranches = useCallback(async () => {
    if (!isMain) return;
    try {
      const res = await axiosInstance.get(APIs.GET_BRANCHES);
      if (res?.data?.status && Array.isArray(res.data.data)) {
        setBranchOptions(
          res.data.data.map((b) => ({
            value: b.compID ?? b.CompID,
            label: b.compName ?? b.CompName ?? `Branch ${b.compID ?? b.CompID}`,
          }))
        );
      }
    } catch (e) {
      console.warn("Failed to load branches", e);
    }
  }, [isMain]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const handleSubmit = async (e, redirectToItems = true) => {
    e.preventDefault();
    const requestingBranchCompID = isMain ? selectedBranch?.value ?? null : compID;
    const parentCompID = isMain ? compID : (parseInt(parentCompId, 10) || 0);
    if (isMain && !requestingBranchCompID) {
      showErrorToast("Please select a requesting branch.");
      return;
    }
    if (!parentCompID) {
      showErrorToast("Invalid company context.");
      return;
    }
    
    // If main branch creates requisition for another branch, set status to "Accepted" so items can be added
    const initialStatus = (isMain && requestingBranchCompID && requestingBranchCompID !== compID) ? "Accepted" : null;
    
    try {
      setLoading(true);
      const res = await axiosInstance.post(APIs.POST_CREATE_REQUISITION, {
        RequestingBranchCompID: requestingBranchCompID,
        ParentCompID: parentCompID,
        RequisitionNumber: requisitionNumber?.trim() || null,
        DueDate: dueDate ? new Date(dueDate).toISOString() : null,
        CreatedBy: currentUserName || "System",
        Notes: notes || null,
        Status: initialStatus,
      });
      const created = res?.data?.data;
      const rid = created?.requisition_ID ?? created?.Requisition_ID;
      if (rid) {
        showSuccessToast("Requisition created successfully.");
        if (redirectToItems) {
          navigate(`/requisition/updateRequisitionItems/${rid}`);
        } else {
          navigate("/requisition/manageRequisitions");
        }
      } else {
        showErrorToast("Failed to create requisition.");
      }
    } catch (err) {
      console.error(err);
      showErrorToast(err?.response?.data?.message || "Failed to create requisition.");
    } finally {
      setLoading(false);
    }
  };

  const renderBreadcrumb = () => (
    <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/dashboard">Home</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/requisition/manageRequisitions">Requisitions</Link>
          </li>
          <li className="breadcrumb-item active">Create Requisition</li>
        </ol>
        <h1 className="page-header mb-0">Create Requisition</h1>
      </div>
      <div className="ms-auto req-toolbar">
        <button type="button" onClick={(e) => handleSubmit(e, true)} className="btn req-btn req-btn-primary">
          Create &amp; Add Items
        </button>
        <button type="button" onClick={(e) => handleSubmit(e, false)} className="btn req-btn req-btn-outline-primary">
          Create only
        </button>
      </div>
    </div>
  );

  const renderBasicInfoPanel = () => (
    <Panel className="mb-0 border-0 shadow-none">
      <PanelHeader noButton className="req-panel-header">
        Requisition details
      </PanelHeader>
      <PanelBody className="req-panel-body">
        <div className="row g-4">
          <div className="col-md-6">
            <FormInput
              label="Requisition Number"
              type="text"
              value={requisitionNumber}
              onchange={(e) => setRequisitionNumber(e.target.value)}
              placeHolder="e.g. REQ-001 (optional)"
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="Due Date"
              type="date"
              value={dueDate}
              onchange={(e) => setDueDate(e.target.value)}
            />
          </div>
          {isMain && (
            <div className="col-md-6">
              <label className="form-label fw-medium" style={{ color: isDarkMode ? "#ffffff" : "#000000" }}>
                Requesting Branch *
              </label>
              <Select
                value={selectedBranch}
                onChange={setSelectedBranch}
                options={branchOptions}
                styles={dropdownStyles}
                placeholder="Select branch"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          )}
          <div className="col-12">
            <label className="form-label fw-medium" style={{ color: isDarkMode ? "#ffffff" : "#000000" }}>
              Notes
            </label>
            <textarea
              className="form-control"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              style={{
                borderRadius: "8px",
                border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                background: isDarkMode ? "#2d3436" : "#F9F9F9",
                color: isDarkMode ? "#ffffff" : "#000000",
                padding: "8px 12px",
              }}
            />
          </div>
        </div>
      </PanelBody>
    </Panel>
  );

  return (
    <div className={`requisition-page ${isDarkMode ? "requisition-page--dark" : ""}`}>
      {loading && <Preloader />}
      {renderBreadcrumb()}
      <div className="card border-0" style={{ position: "relative", zIndex: 1 }}>
        <div className="tab-content p-3">
          <div className="tab-pane fade show active">{renderBasicInfoPanel()}</div>
        </div>
      </div>
    </div>
  );
};

export default CreateRequisition;
